/**
 * Serviço de Dashboard do Sistema MEDICARE
 * Gerencia todas as operações relacionadas ao dashboard e estatísticas
 */

import { ApiService } from './api';
import { ApiResponse } from '../types';

// Interface para estatísticas do dashboard
export interface EstatisticasDashboard {
  totalUsuarios: number;
  consultasHoje: number;
  totalConsultas: number;
  satisfacaoMedia: number;
  consultasRecentes?: ConsultaRecente[];
  medicosAtivos?: MedicoAtivo[];
  performanceSemanal?: PerformanceSemanal[];
}

// Interface para consulta recente
export interface ConsultaRecente {
  id: string;
  paciente: string;
  medico: string;
  data: string;
  status: string;
  tipo: string;
}

// Interface para médico ativo
export interface MedicoAtivo {
  id: string;
  nome: string;
  especialidade: string;
  consultasHoje: number;
  status: 'online' | 'ocupado' | 'offline';
}

// Interface para performance semanal
export interface PerformanceSemanal {
  dia: string;
  consultas: number;
  satisfacao: number;
}

// Interface para consultas por período
export interface ConsultasPorPeriodo {
  periodo: string;
  total: number;
  concluidas: number;
  canceladas: number;
  agendadas: number;
}

// Interface para dados de evolução
export interface EvolucaoData {
  data: string;
  consultas: number;
  usuarios: number;
}

// Interface para resposta paginada de consultas recentes
export interface ConsultasRecentesPaginadas {
  consultas: ConsultaRecente[];
  paginacao: {
    pagina_atual: number;
    total_paginas: number;
    total_itens: number;
    itens_por_pagina: number;
  };
}

export class DashboardService {
  private static readonly BASE_URL = '/dashboard';

  /**
   * Obter estatísticas gerais do dashboard
   */
  static async obterEstatisticas(): Promise<ApiResponse<EstatisticasDashboard>> {
    const response = await ApiService.get<EstatisticasDashboard>(`${this.BASE_URL}/estatisticas`);
    
    // Se a resposta tem a estrutura padrão do backend, extrair os dados
    if (response.data && typeof response.data === 'object' && 'dados' in response.data) {
      return {
        ...response,
        data: response.data.dados as EstatisticasDashboard
      };
    }
    
    return response;
  }

  /**
   * Obter consultas por período
   */
  static async obterConsultasPorPeriodo(
    dataInicio?: string,
    dataFim?: string
  ): Promise<ApiResponse<ConsultasPorPeriodo[]>> {
    const params = new URLSearchParams();
    if (dataInicio) params.append('dataInicio', dataInicio);
    if (dataFim) params.append('dataFim', dataFim);
    
    const queryString = params.toString();
    const url = queryString 
      ? `${this.BASE_URL}/consultas-por-periodo?${queryString}`
      : `${this.BASE_URL}/consultas-por-periodo`;
    
    return ApiService.get<ConsultasPorPeriodo[]>(url);
  }

  /**
   * Obter resumo de estatísticas (versão simplificada)
   */
  static async obterResumo(): Promise<ApiResponse<{
    totalUsuarios: number;
    consultasHoje: number;
    totalConsultas: number;
    satisfacaoMedia: number;
  }>> {
    const response = await this.obterEstatisticas();
    
    if (response.success && response.data) {
      return {
        success: true,
        data: {
          totalUsuarios: response.data.totalUsuarios,
          consultasHoje: response.data.consultasHoje,
          totalConsultas: response.data.totalConsultas,
          satisfacaoMedia: response.data.satisfacaoMedia,
        }
      };
    }
    
    return response as ApiResponse<{
      totalUsuarios: number;
      consultasHoje: number;
      totalConsultas: number;
      satisfacaoMedia: number;
    }>;
  }
  /**
   * Obter dados de evolução de consultas e usuários nos últimos 30 dias
   */
  static async obterEvolucao(): Promise<ApiResponse<EvolucaoData[]>> {
    const response = await ApiService.get<EvolucaoData[]>(`${this.BASE_URL}/evolucao`);
    
    // Verificar se a resposta tem a estrutura esperada e extrair os dados
    if (response.success && response.data && typeof response.data === 'object' && 'dados' in response.data) {
      return {
        success: true,
        data: (response.data as any).dados as EvolucaoData[]
      };
    }
    
    return response;
  }

  /**
   * Obter consultas recentes com paginação
   * Se usuário for médico, retorna apenas suas consultas
   * Se usuário for admin, retorna todas as consultas
   */
  static async obterConsultasRecentes(
    page: number = 1,
    limit: number = 10
  ): Promise<ApiResponse<ConsultasRecentesPaginadas>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    const response = await ApiService.get<ConsultasRecentesPaginadas>(
      `${this.BASE_URL}/consultas-recentes?${params.toString()}`
    );
    
    // Verificar se a resposta tem a estrutura esperada e extrair os dados
    if (response.success && response.data && typeof response.data === 'object' && 'dados' in response.data) {
      return {
        success: true,
        data: (response.data as any).dados as ConsultasRecentesPaginadas
      };
    }
    
    return response;
  }
}

export default DashboardService;